import type { ButtonHTMLAttributes, FunctionComponent, MouseEvent, ReactElement, ReactNode } from 'react';
import { useCallback, useRef } from 'react';

import { useAudioTrigger } from '../../../../controllers/audioController/hooks';
import { SessionStorage } from '../../../../storage/session/SessionStorage';
import { Badge, BadgeSize } from '../../../controls/badge/Badge';
import { Icon } from '../../../icons/icon';
import { Icons } from '../../../icons/iconNames';
import { LobbyButtonAnimation } from '../../../lobbyButtonAnimation/LobbyButtonAnimation';
import lobbyAnimationData from '../assets/lobby.json';
import { LobbyFallback } from '../assets/LobbyFallback';

import styles from './navDrawerButton.module.css';

const animation = {
  animationData: lobbyAnimationData,
  autoplay: false,
  loop: false,
};

export interface NavDrawerButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  icon: keyof Icons;
  badgeValue?: string | number;
  lobby?: boolean;
  revealed?: boolean;
  showBadge?: boolean;
  showCrown?: boolean;
}

export const NavDrawerButton: FunctionComponent<NavDrawerButtonProps> = ({
  badgeValue,
  children,
  icon,
  lobby,
  onClick,
  revealed,
  showBadge,
  showCrown,
  ...rest
}: NavDrawerButtonProps): ReactElement => {
  const completeRef = useRef<boolean>(SessionStorage.get('lobbyAnimationComplete') === 'true');

  const onComplete = useCallback((): void => {
    completeRef.current = true;
    SessionStorage.set('lobbyAnimationComplete', 'true');
  }, []);

  const audioTrigger = useAudioTrigger();
  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    audioTrigger.play({ name: 'menuClick' });
    onClick?.(event);
  };

  return (
    <button className={styles.navDrawerButton} onClick={handleClick} type="button" {...rest}>
      <span className={styles.navDrawerButton__iconContainer}>
        {lobby ? (
          <LobbyButtonAnimation
            animation={animation}
            canPlay={!!revealed}
            className={styles.navDrawerButton__lobbyAnimation}
            complete={completeRef.current}
            fallback={<LobbyFallback className={styles.navDrawerButton__lobbyAnimation} />}
            onComplete={onComplete}
          />
        ) : (
          <span className={styles.navDrawerButton__icon}>
            <Icon icon={icon} />
            {showCrown ? <span className={styles.navDrawerButton__crown} /> : null}
            {showBadge ? (
              <Badge value={badgeValue} size={BadgeSize.Large} impact className={styles.navDrawerButton__badge} />
            ) : null}
          </span>
        )}
      </span>
      <span className={styles.navDrawerButton__label}>{children}</span>
    </button>
  );
};
